import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Form, Modal, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import Select from "react-select";
import { getRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";
import { getAccountingTransactionText } from "./accounting-transaction-text";
import { buildTrfIndex, fetchTrfEntries, findTrfMatches, getTrfSectionLabel } from "./trf-duplicate-check";

const formatNumber = (value) => Number(value || 0).toLocaleString("id-ID");
const parseNumberInput = (value) => Number(String(value || "").replace(/[^0-9]/g, "")) || 0;
const toDateOnly = (value) => (value ? String(value).slice(0, 10) : "");

const formatShortDate = (value) => {
  if (!value) return "-";
  const date = new Date(`${toDateOnly(value)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "2-digit" });
};

const getReservationIncomeId = (row) =>
  row?.reservation_income_id || row?.reservation_income?.id || null;

// Ambil SEMUA transaksi Accounting V2 di periode dalam 1 request (all=1), urutan sesuai BE
// (= urutan tabel Accounting V2). Fallback ke pagination hanya kalau BE ternyata tetap membagi per halaman.
const fetchAccountingV2Page = (dateRange, page) => getRequest(`/reservation-income-v2${buildQueryString({
  start_date: dateRange.start,
  end_date: dateRange.end,
  all: 1,
  ...(page > 1 && { page }),
})}`);

const fetchAllAccountingV2Transactions = async (dateRange) => {
  const firstPage = await fetchAccountingV2Page(dateRange, 1);
  const firstRows = Array.isArray(firstPage?.data) ? firstPage.data : [];
  const lastPage = Number(firstPage?.pagination?.last_page) || 1;
  if (lastPage <= 1) return firstRows;

  const restPages = await Promise.all(
    Array.from({ length: lastPage - 1 }, (_, index) => fetchAccountingV2Page(dateRange, index + 2))
  );
  const seenIds = new Set();
  return [firstRows, ...restPages.map((res) => (Array.isArray(res?.data) ? res.data : []))]
    .flat()
    .filter((row) => {
      if (seenIds.has(row.id)) return false;
      seenIds.add(row.id);
      return true;
    });
};

// Transaksi Accounting V2 dalam periode Pembukuan: pilihan di popup Trf sekaligus acuan urutan tabel Trf.
export const useAccountingV2Transactions = (enabled, dateRange) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const loadedRangeRef = useRef("");

  useEffect(() => {
    const rangeKey = `${dateRange.start}|${dateRange.end}`;
    if (!enabled || loadedRangeRef.current === rangeKey) return undefined;
    let cancelled = false;

    setLoading(true);
    setError("");
    fetchAllAccountingV2Transactions({ start: dateRange.start, end: dateRange.end })
      .then((rows) => {
        if (cancelled) return;
        setTransactions(rows);
        loadedRangeRef.current = rangeKey;
      })
      .catch((err) => {
        if (cancelled) return;
        setTransactions([]);
        setError(err?.message || "Gagal memuat transaksi Accounting V2.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, dateRange.start, dateRange.end]);

  return { transactions, loading, error };
};

const BookkeepingTransferModal = ({
  show,
  tabLabel,
  dateRange,
  defaultEntryType = "credit",
  row,
  saving = false,
  deleting = false,
  onHide,
  onSave,
  onDelete,
  moveTarget,
  onMove,
  hideEntryType = false,
  transactions = [],
  transactionsLoading: loading = false,
  transactionsError: error = "",
  section,
}) => {
  const isEdit = Boolean(row?.apiId);
  const [form, setForm] = useState({});
  const [formError, setFormError] = useState("");
  // Data Trf yang sudah ada di periode ini, untuk peringatan transaksi dobel.
  const [trfIndex, setTrfIndex] = useState(null);

  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    setTrfIndex(null);
    fetchTrfEntries({ start: dateRange.start, end: dateRange.end })
      .then((entries) => {
        if (!cancelled) setTrfIndex(buildTrfIndex(entries));
      })
      .catch(() => {
        // Gagal cek dobel tidak boleh menghalangi input; peringatan saja yang tidak muncul.
      });
    return () => {
      cancelled = true;
    };
  }, [show, dateRange.start, dateRange.end]);

  const getMatchesFor = (candidate) => (trfIndex
    ? findTrfMatches(candidate, trfIndex, { excludeIds: row?.apiId ? [row.apiId] : [] })
    : []);
  const formMatches = getMatchesFor({ reservationId: form.reservationId, text: form.description, date: form.date });
  const formMatchesInSection = formMatches.filter((match) => match.section === section);

  useEffect(() => {
    if (!show) return;
    const amount = Number(row?.debit || row?.credit || 0);
    setForm({
      reservationId: row?.reservationId || null,
      reservationIncomeId: row?.reservationIncomeId || null,
      date: row?.rawDate || toDateOnly(new Date().toISOString()),
      description: row?.description || "",
      entryType: row?.debit > 0 ? "debit" : row?.credit > 0 ? "credit" : row?.entryType || defaultEntryType,
      amount,
    });
    setFormError("");
  }, [show, row, defaultEntryType]);

  const options = useMemo(() => [...transactions]
    .sort((a, b) => String(a.reservation_date || "").localeCompare(String(b.reservation_date || "")))
    .map((transaction) => {
      const text = getAccountingTransactionText(transaction);
      return {
        value: transaction.id,
        label: text,
        text,
        date: toDateOnly(transaction.reservation_date),
        paymentBill: Number(transaction.payment_bill) || 0,
        code: transaction.code || transaction.reservation_code || "",
        reservationIncomeId: getReservationIncomeId(transaction),
        // Tab Trf tempat transaksi ini sudah ada (untuk badge peringatan dobel di daftar pilihan).
        existingSections: trfIndex
          ? [...new Set(findTrfMatches(
            { reservationId: transaction.id, text, date: transaction.reservation_date },
            trfIndex
          ).map((match) => match.section))]
          : [],
      };
    }), [transactions, trfIndex]);

  const selectedOption = useMemo(() => {
    if (!form.reservationId) return null;
    return options.find((option) => option.value === form.reservationId) || {
      value: form.reservationId,
      label: form.description || `Reservasi #${form.reservationId}`,
      text: form.description,
      date: form.date,
      paymentBill: 0,
      code: "",
    };
  }, [form.reservationId, form.description, form.date, options]);

  const updateForm = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const handleSelectTransaction = (option) => {
    if (!option) {
      setForm((current) => ({ ...current, reservationId: null, reservationIncomeId: null }));
      return;
    }
    // Tanggal otomatis mengikuti tanggal transaksi di Accounting V2 (tetap bisa diganti).
    setForm((current) => ({
      ...current,
      reservationId: option.value,
      reservationIncomeId: option.reservationIncomeId,
      description: option.text,
      date: option.date || current.date,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.date) {
      setFormError("Tanggal wajib diisi.");
      return;
    }
    if (!String(form.description || "").trim()) {
      setFormError("Pilih transaksi Accounting V2 atau isi keterangan manual.");
      return;
    }
    if (formMatchesInSection.length > 0) {
      const confirmation = await Swal.fire({
        icon: "warning",
        title: "Transaksi ini sudah ada",
        text: `Transaksi yang sama sudah ada di ${getTrfSectionLabel(section)}. Tetap simpan (jadi dobel)?`,
        showCancelButton: true,
        confirmButtonText: "Tetap simpan",
        cancelButtonText: "Batal",
        confirmButtonColor: "#dc3545",
      });
      if (!confirmation.isConfirmed) return;
    }
    setFormError("");
    onSave(form);
  };

  const formatOptionLabel = (option, { context }) => {
    if (context === "value") return option.text || option.label;
    return (
      <div className="bookkeeping-transfer-option">
        <span className="bookkeeping-transfer-option-date">{formatShortDate(option.date)}</span>
        <span className="bookkeeping-transfer-option-text">{option.text}</span>
        {option.existingSections?.map((existingSection) => (
          <Badge key={existingSection} bg={existingSection === section ? "danger" : "warning"} text={existingSection === section ? undefined : "dark"}>
            Sudah di {getTrfSectionLabel(existingSection)}
          </Badge>
        ))}
        {option.paymentBill > 0 && (
          <span className="bookkeeping-transfer-option-amount">{formatNumber(option.paymentBill)}</span>
        )}
      </div>
    );
  };

  const busy = saving || deleting;

  return (
    <Modal show={show} onHide={busy ? undefined : onHide} centered size="lg">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton={!busy}>
          <Modal.Title>{isEdit ? "Edit" : "Tambah"} {tabLabel}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold small">Transaksi Accounting V2</Form.Label>
            <Select
              classNamePrefix="bookkeeping-transfer-select"
              options={options}
              value={selectedOption}
              onChange={handleSelectTransaction}
              isClearable
              isLoading={loading}
              isDisabled={busy}
              placeholder={loading ? "Memuat transaksi..." : "Cari kode, tamu, hotel, agent..."}
              noOptionsMessage={() => "Transaksi tidak ditemukan di periode ini"}
              formatOptionLabel={formatOptionLabel}
              filterOption={(option, input) => {
                const keyword = input.trim().toLowerCase();
                if (!keyword) return true;
                const { text, code, date } = option.data;
                return `${text} ${code} ${date} ${formatShortDate(date)}`.toLowerCase().includes(keyword);
              }}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 2000 }) }}
            />
            <Form.Text className="text-muted">
              Opsional. Data dari Accounting V2 periode {dateRange.start} s/d {dateRange.end}.
              Kosongkan untuk transaksi manual (mis. transfer ambil tunai).
            </Form.Text>
            {error && <div className="text-danger small mt-1">{error}</div>}
            {formMatches.length > 0 && (
              <div className={`small mt-2 fw-bold ${formMatchesInSection.length ? "text-danger" : "text-warning"}`}>
                ⚠ Transaksi ini sudah ada di {[...new Set(formMatches.map((match) => getTrfSectionLabel(match.section)))].join(", ")}.
                {formMatchesInSection.length ? " Menyimpan lagi akan membuat data dobel." : ""}
              </div>
            )}
          </Form.Group>

          <div className="row g-3">
            <div className="col-md-4">
              <Form.Group>
                <Form.Label className="fw-bold small">Tanggal</Form.Label>
                <Form.Control
                  type="date"
                  value={form.date || ""}
                  onChange={(event) => updateForm("date", event.target.value)}
                  // Klik di mana saja pada field langsung membuka popup kalender.
                  onClick={(event) => event.currentTarget.showPicker?.()}
                  disabled={busy}
                />
                {form.reservationId && (
                  <Form.Text className="text-muted">Otomatis dari tanggal transaksi Accounting V2.</Form.Text>
                )}
              </Form.Group>
            </div>
            {!hideEntryType && (
            <div className="col-md-4">
              <Form.Group>
                <Form.Label className="fw-bold small">Jenis</Form.Label>
                <div className="d-flex gap-3 pt-1">
                  <Form.Check
                    type="radio"
                    id="bookkeeping-transfer-credit"
                    label="Kredit"
                    checked={form.entryType === "credit"}
                    onChange={() => updateForm("entryType", "credit")}
                    disabled={busy}
                  />
                  <Form.Check
                    type="radio"
                    id="bookkeeping-transfer-debit"
                    label="Debet"
                    checked={form.entryType === "debit"}
                    onChange={() => updateForm("entryType", "debit")}
                    disabled={busy}
                  />
                </div>
              </Form.Group>
            </div>
            )}
            <div className={hideEntryType ? "col-md-8" : "col-md-4"}>
              <Form.Group>
                <Form.Label className="fw-bold small">{hideEntryType ? "Nominal (Debet)" : "Nominal"}</Form.Label>
                <Form.Control
                  inputMode="numeric"
                  className="text-end fw-bold"
                  value={form.amount ? formatNumber(form.amount) : ""}
                  placeholder="Boleh dikosongkan"
                  onChange={(event) => updateForm("amount", parseNumberInput(event.target.value))}
                  disabled={busy}
                />
              </Form.Group>
            </div>
            <div className="col-12">
              <Form.Group>
                <Form.Label className="fw-bold small">Keterangan</Form.Label>
                <Form.Control
                  value={form.description || ""}
                  onChange={(event) => updateForm("description", event.target.value)}
                  placeholder="Keterangan transaksi..."
                  disabled={busy}
                />
              </Form.Group>
            </div>
          </div>

          {formError && <div className="alert alert-warning py-2 mt-3 mb-0">{formError}</div>}
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <div className="d-flex gap-2">
            {isEdit && (
              <Button variant="outline-danger" onClick={() => onDelete(row)} disabled={busy}>
                {deleting ? <Spinner animation="border" size="sm" /> : "Hapus"}
              </Button>
            )}
            {isEdit && moveTarget && onMove && (
              <Button variant="outline-primary" onClick={() => onMove(row)} disabled={busy}>
                Pindah ke {moveTarget.label}
              </Button>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button variant="light" className="border" onClick={onHide} disabled={busy}>Batal</Button>
            <Button type="submit" variant="primary" disabled={busy}>
              {saving ? <><Spinner animation="border" size="sm" className="me-1" /> Menyimpan...</> : "Simpan"}
            </Button>
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default BookkeepingTransferModal;
